/**
 * Page Performance — route keys (pure, no React/DOM imports).
 *
 * A page is keyed by its PATH ONLY: a change of query string or hash (e.g.
 * `?tab=`) is never a new load, and neither ends up in the key. Variable
 * segments are collapsed so every Space / List / Document shares one key:
 *
 *   /app/spaces/3f2c…-uuid/lists/42  →  /app/spaces/:id/lists/:id
 *
 * The server re-normalises defensively with the same rule.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i
const DIGITS_RE = /^\d+$/

export const MAX_ROUTE_LENGTH = 300

/** True when one path segment is an identifier (UUID, 24-hex ObjectId or
 *  all digits) and must be replaced by `:id`. */
export function isIdSegment(segment) {
  if (!segment) return false
  return UUID_RE.test(segment) || OBJECT_ID_RE.test(segment) || DIGITS_RE.test(segment)
}

/** Strip query + hash, drop a trailing slash and collapse id segments.
 *  Shared by the API-path normaliser. Never throws. */
export function normalisePath(input) {
  let path = typeof input === 'string' ? input : ''
  const cut = path.search(/[?#]/)
  if (cut !== -1) path = path.slice(0, cut)
  if (!path.startsWith('/')) path = `/${path}`
  const segments = path
    .split('/')
    .filter((s, i) => i === 0 || s !== '')
    .map((s) => (isIdSegment(s) ? ':id' : s))
  let out = segments.join('/')
  if (out.length > 1 && out.endsWith('/')) out = out.slice(0, -1)
  return out || '/'
}

/** A route segment the app itself declares: lower-case words and hyphens. */
const WORD_SEGMENT_RE = /^[a-z][a-z0-9-]*$/

/**
 * Route key for a browser pathname (≤ 300 characters). After the id rule,
 * any segment that is not a plain lower-case word (`^[a-z][a-z0-9-]*$`)
 * becomes `:param` — free text in a URL (an email, a mixed-case token, a
 * dotted name, a mistyped NotFound path) never reaches the key. Placeholders
 * already in a key (`:id`, `:param`) are kept, so the rule is idempotent.
 * The server applies the same rule.
 */
export function normalizeRoute(pathname) {
  return applyParamRule(normalisePath(pathname)).slice(0, MAX_ROUTE_LENGTH)
}

/** The `:param` rule on an already id-normalised path: any segment that is
 *  not a plain lower-case word or a placeholder becomes `:param`. Also used
 *  for API call paths, so an email or token inside an API path is never
 *  stored either (every static API segment is a lower-case word). */
export function applyParamRule(path) {
  const out = String(path)
    .split('/')
    .map((s, i) => {
      if (i === 0 || s === '' || s === ':id' || s === ':param') return s
      return WORD_SEGMENT_RE.test(s) ? s : ':param'
    })
    .join('/')
  return out || '/'
}

/** `/` is a pass-through boot route (it renders nothing, then redirects to
 *  the home page or sign-in): never a measured page, but a full load that
 *  starts there is handed to the first real route. */
export function isBootRoute(pathname) {
  return normalizeRoute(pathname) === '/'
}

/*
 * Routes that are NEVER measured — the public / pre-login routes declared
 * in src/App.jsx (outside every <ProtectedRoute>):
 *
 *   /                 → boot blank, then redirect to the home page (or sign-in);
 *                       never measured itself, but a full load starting here
 *                       is handed to the first real route (`isBootRoute`)
 *   /sign-in          → sign-in; also where the Google SSO round trip lands
 *                       (the OAuth callback itself is served by the API)
 *   /sign-up          → open sign-up
 *   /forgot-password  → password-reset request
 *   /reset-password   → password reset (and invite-set-password) link target
 *   /join             → join-company invitation acceptance
 *   /maintenance      → maintenance-mode landing
 *
 * The spec's lesson: a session can resolve while the person is still on the
 * sign-in screen, about to be redirected — that open is not a page load.
 * The app has no separate verification, two-factor or logout screens; the
 * legacy `/admin/*` redirects are left to redirect absorption instead.
 * Matched exactly or as a prefix followed by `/`.
 */
export const EXCLUDED_ROUTES = Object.freeze([
  '/',
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/join',
  '/maintenance',
])

export function isExcludedRoute(pathname) {
  const route = normalizeRoute(pathname)
  return EXCLUDED_ROUTES.some(
    (r) => route === r || (r !== '/' && route.startsWith(`${r}/`)),
  )
}
